/**
 * Purchase masters — organizations, groups, vendors, materials.
 * Calls the Spring Boot API.
 */
import type { CreateVendorInput, PurchaseGroup, PurchaseOrganization, PurchaseVendor } from '@shared/types/purchaseMaster';
import { api, qs } from '../../../renderer/services/http';

export interface PurchaseMaterial {
  id: string;
  item_code: string;
  item_name: string;
  uom: string;
  item_type: string;
  unit_price: number;
  gst_rate: number;
  current_stock: number;
  reorder_level: number;
  hsn_code: string;
  is_active: boolean;
  is_purchasable: boolean;
  created_at: string;
}

export const purchaseMasterMockApi = {
  getPurchaseOrgs: async () => api<PurchaseOrganization[]>('/api/purchase/orgs'),
  getPurchaseGroups: async () => api<PurchaseGroup[]>('/api/purchase/groups'),
  getPurchaseOrgByCode: async (code: string) => api<PurchaseOrganization>(`/api/purchase/orgs/by-code/${encodeURIComponent(code)}`),
  getPurchaseGroupByCode: async (code: string) => api<PurchaseGroup>(`/api/purchase/groups/by-code/${encodeURIComponent(code)}`),
  createPurchaseOrg: async (input: { name: string; code?: string }) =>
    api('/api/purchase/orgs', { method: 'POST', body: JSON.stringify(input) }),
  createPurchaseGroup: async (input: { name: string; code?: string }) =>
    api('/api/purchase/groups', { method: 'POST', body: JSON.stringify(input) }),
  peekNextCodes: async () => api('/api/purchase/codes/next'),

  getVendors: async (filters?: {
    search?: string;
    is_active?: string;
    purchase_org_id?: string;
    purchase_group_id?: string;
  }) =>
    api<PurchaseVendor[]>(
      `/api/purchase/vendors${qs({
        search: filters?.search,
        isActive: filters?.is_active,
        orgId: filters?.purchase_org_id,
        groupId: filters?.purchase_group_id,
      })}`
    ),

  getVendorById: async (id: string) => api<PurchaseVendor>(`/api/purchase/vendors/${id}`),
  getPayableVendors: async () => api<PurchaseVendor[]>('/api/purchase/vendors/payable'),
  createVendor: async (_tenantId: string, input: CreateVendorInput) =>
    api('/api/purchase/vendors', { method: 'POST', body: JSON.stringify(input) }),
  updateVendor: async (id: string, input: Partial<CreateVendorInput>) =>
    api(`/api/purchase/vendors/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  deactivateVendor: async (id: string) =>
    api(`/api/purchase/vendors/${id}/deactivate`, { method: 'POST' }),

  getMaterials: async (filters?: { activeOnly?: boolean; purchasableOnly?: boolean; search?: string }) => {
    const res = await api<PurchaseMaterial[]>(`/api/inventory/materials${qs({ search: filters?.search, activeOnly: filters?.activeOnly })}`);
    if (!res.success || !filters?.purchasableOnly) return res;
    return { success: true as const, data: (res.data || []).filter((m) => m.is_purchasable && m.is_active) };
  },

  createMaterial: async (input: {
    item_code?: string;
    item_name: string;
    uom?: string;
    item_type?: string;
    unit_price?: number;
    gst_rate?: number;
    reorder_level?: number;
    hsn_code?: string;
    is_active?: boolean;
  }) => api('/api/inventory/materials', { method: 'POST', body: JSON.stringify(input) }),
};
